import Hls from 'hls.js';
import assign from 'lodash/assign';

import HLS_JS_DEFAULTS from './defaults/HlsJS';


export default class HlsJsSourceHandler {
    constructor(source, tech, options) {
        this._player = tech;
        this._options = assign({}, HLS_JS_DEFAULTS, options);
        this._errorCounts = {};
        this._video = this._player.el();

        this._hls = new Hls(this._options);
        this._hls.on(Hls.Events.ERROR, this._onError.bind(this));
        this._hls.on(Hls.Events.MANIFEST_PARSED, this._onManifestParsed.bind(this));
        this._hls.on(Hls.Events.LEVEL_LOADED, this._onLevelLoaded.bind(this));
        this._hls.on(Hls.Events.MEDIA_ATTACHED, this._onMediaAttached.bind(this, source));

        this._hls.attachMedia(this._video);

        this._player.hlsJS = this._hls; // access on player with player.tech_.hlsJS
        this.dispose = this._dispose; // access on player with player.tech_.dispose();
        this.duration = this._duration;
    }

    _dispose() {
        this._hls.detachMedia();
        this._hls.destroy();
    }

    _duration() {
        return this._duration || this._video.duration || 0;
    }

    _load(source) {
        this._hls.loadSource(source.src);
    }

    _onLevelLoaded(event, data) {
        this._duration = data.details.live ? Infinity : data.details.totalduration;

        this._player.trigger('hlsLevelLoaded', data);
    }

    _onMediaAttached(source, name, data) {
        this._player.trigger('hlsMediaAttached', data);

        this._load(source);
    }

    _onManifestParsed(event, data) {
        let cleanTracklist = [];

        if (data.levels.length > 1) {
            const autoLevel = {
                id: -1,
                label: 'auto',
                selected: -1 === this._hls.manualLevel
            };
            cleanTracklist.push(autoLevel);
        }

        data.levels.forEach((level, index) => {
            const quality = {
                id: index,
                selected: index === this._hls.manualLevel,
                label: _levelLabel(level)
            };

            cleanTracklist.push(quality);
        });

        const payload = {
            qualityData: { video: cleanTracklist },
            qualitySwitchCallback: this._switchQuality
        };

        this._player.trigger('hlsLoadedQualityData', payload);

        this._player.trigger('hlsManifestParsed', data);

        function _levelLabel(level) {
            if (level.height) return level.height + 'p';
            else if (level.width) return Math.round(level.width * 9 / 16) + 'p';
            else if (level.bitrate) return (level.bitrate / 1000) + 'kbps';
            else return 0;
        }
    }

    _switchQuality(qualityId, trackType) {
        this._hls.nextLevel = qualityId;
    }

    _onError(event, data) {
        var error = {
            message: 'HLS.js error: ' + data.type + ' || fatal: ' + data.fatal + ' || details: ' + data.details,
        };

        // increment/set error count
        this._errorCounts[data.type] ? this._errorCounts[data.type] += 1 : this._errorCounts[data.type] = 1;

        if (data.fatal) {
            switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                    error.response = data.response;
                    this._player.error = () => error;
                    this._player.trigger('error', error);
                    break;

                case Hls.ErrorTypes.MEDIA_ERROR:
                    if (this._errorCounts[Hls.ErrorTypes.MEDIA_ERROR] === 1) {
                        console.info('trying to recover media error');
                        this._hls.recoverMediaError();
                    } else if (this._errorCounts[Hls.ErrorTypes.MEDIA_ERROR] === 2) {
                        console.info('2nd try to recover media error (by swapping audio codec');
                        this._hls.swapAudioCodec();
                        this._hls.recoverMediaError();
                    } else if (this._errorCounts[Hls.ErrorTypes.MEDIA_ERROR] > 2) {
                        this._player.error = () => error;
                        this._player.trigger('error');
                    }
                    break;

                default: // cannot recover
                    this._dispose();
                    this._player.error = () => error;
                    this._player.trigger('error', error);
                    break;
            }
        }

        // trigger hls specific error details
        switch (data.details) {
            case Hls.ErrorDetails.MANIFEST_LOAD_ERROR:
            case Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT:
            case Hls.ErrorDetails.MANIFEST_PARSING_ERROR:
            case Hls.ErrorDetails.LEVEL_LOAD_ERROR:
            case Hls.ErrorDetails.LEVEL_LOAD_TIMEOUT:
            case Hls.ErrorDetails.LEVEL_SWITCH_ERROR:
            case Hls.ErrorDetails.FRAG_LOAD_ERROR:
                this._player.trigger('hlsLoadError', data);
                break;
            case Hls.ErrorDetails.FRAG_LOOP_LOADING_ERROR:
            case Hls.ErrorDetails.FRAG_LOAD_TIMEOUT:
            case Hls.ErrorDetails.FRAG_PARSING_ERROR:
            case Hls.ErrorDetails.BUFFER_APPEND_ERROR:
            case Hls.ErrorDetails.BUFFER_APPENDING_ERROR:
                // do nothing for now
                break;
            default:
                break;

        }
    }
}




// WEBPACK FOOTER //
// ./src/player/HlsJSSourceHandler.js